import { SearchInput, Button } from '@/components/ui';
import { useGetCompanies } from '@/services/queries/company';
import {
	Users,
	Plus,
	Pencil,
	ChevronDown,
	ChevronUp,
	Settings,
	LayoutDashboard,
	FileText,
	Building2,
	Mail,
	CheckCircle2,
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { CompanyModal } from './components/CompanyModal';
import { EditCompanyModal } from './components/EditCompanyModal';

const formatCNPJ = (value?: string | null) => {
	if (!value) return '—';

	return value
		.replace(/\D/g, '')
		.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const normalizeText = (value?: string | null) => value?.toLowerCase() ?? '';

const getCompanyDisplayName = (company: Companies.Company) =>
	company.trade_name?.trim() ||
	company.legal_name?.trim() ||
	'Empresa sem nome';

interface CompanyListItemProps {
	company: Companies.Company;
	onEdit: (company: Companies.Company) => void;
	onNavigateToDetails: (id: string) => void;
}

const CompanyListItem = ({
	company,
	onEdit,
	onNavigateToDetails,
}: CompanyListItemProps) => {
	const navigate = useNavigate();
	const [imgError, setImgError] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const imgUrl = company.logo_img_url ?? company.banner_img_url;
	const showFallback = !imgUrl || imgError;
	const companyName = getCompanyDisplayName(company);

	return (
		<div className="group rounded-2xl border border-stone-100 bg-white p-4 shadow-sm transition-all duration-200 hover:border-stone-200">
			{/* Cabeçalho da empresa */}
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-center gap-4">
					{showFallback ? (
						<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-stone-100 text-lg font-bold uppercase text-stone-800">
							{companyName.charAt(0)}
						</div>
					) : (
						<img
							src={imgUrl ?? ''}
							alt={companyName}
							className="h-12 w-12 flex-shrink-0 rounded-xl border border-black/20 object-cover object-center"
							onError={() => setImgError(true)}
						/>
					)}

					<div className="flex min-w-0 flex-col">
						<h3 className="truncate text-base font-bold leading-tight text-stone-900">
							{companyName}
						</h3>
						<div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] font-bold uppercase tracking-tight text-stone-400">
							<div className="flex items-center gap-1">
								<Users className="h-3.5 w-3.5" />
								<span>
									{company._count?.employees || 0}{' '}
									colaboradores
								</span>
							</div>
							<span className="hidden text-stone-200 sm:inline">
								•
							</span>
							<span className="text-xs text-stone-500">
								{formatCNPJ(company.cnpj)}
							</span>
						</div>
					</div>
				</div>

				<div className="flex flex-shrink-0 flex-col items-end gap-2">
					<span
						className={`rounded-full px-3 py-1 text-[9px] font-black uppercase tracking-widest ${
							company.active
								? 'bg-emerald-50 text-emerald-600'
								: 'bg-rose-50 text-rose-600'
						}`}
					>
						{company.active ? 'Ativo' : 'Inativo'}
					</span>
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								navigate(
									`/admin/empresas/${company.id}/dashboard`,
								);
							}}
							className="flex items-center justify-center rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
							title="Painel da empresa"
						>
							<LayoutDashboard size={16} />
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onEdit(company);
							}}
							className="flex items-center justify-center rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
							title="Editar empresa"
						>
							<Pencil size={16} />
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onNavigateToDetails(company.id);
							}}
							className="flex items-center justify-center rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
							title="Detalhes e configurações"
						>
							<Settings size={16} />
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setIsExpanded((prev) => !prev);
							}}
							className="flex items-center justify-center rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
							title={
								isExpanded
									? 'Recolher informações'
									: 'Ver informações'
							}
						>
							{isExpanded ? (
								<ChevronUp size={16} />
							) : (
								<ChevronDown size={16} />
							)}
						</button>
					</div>
				</div>
			</div>

			{/* Informações detalhadas (grid 2x2) */}
			{isExpanded && (
				<div className="mt-4 grid grid-cols-1 gap-4 border-t border-stone-100 pt-4 text-sm text-stone-900 sm:grid-cols-2">
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							Razão social
						</p>
						<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
							<FileText className="h-4 w-4 text-stone-500" />
							<span>{company.legal_name || '—'}</span>
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							E-mail de faturamento
						</p>
						<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
							<Mail className="h-4 w-4 text-stone-500" />
							<span>{company.billing_email || '—'}</span>
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							Nome fantasia
						</p>
						<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
							<Building2 className="h-4 w-4 text-stone-500" />
							<span>{company.trade_name || '—'}</span>
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							Status da empresa
						</p>
						<p className="mt-1 flex items-center gap-2 text-sm font-semibold text-stone-800">
							<CheckCircle2
								className={`h-4 w-4 ${
									company.active
										? 'text-emerald-500'
										: 'text-stone-400'
								}`}
							/>
							<span>
								{company.active
									? 'Empresa ativa no sistema'
									: 'Empresa inativa no sistema'}
							</span>
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

const Companies = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [companyToEdit, setCompanyToEdit] =
		useState<Companies.Company | null>(null);

	const search = searchParams.get('search') || '';
	const { data: companiesResponse, isFetching } = useGetCompanies({
		page: 1,
		limit: 100,
	});

	const list = Array.isArray(companiesResponse)
		? companiesResponse
		: companiesResponse?.data || [];

	const filteredList = list.filter((c) =>
		normalizeText(c?.trade_name ?? c?.legal_name).includes(
			normalizeText(search),
		),
	);

	return (
		<div className="space-y-6 pt-4">
			<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="w-full sm:max-w-md">
					<SearchInput
						placeholder="Buscar empresa..."
						className="bg-inherit"
					/>
				</div>
				<Button
					text="Nova empresa"
					className="w-full rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
					startIcon={
						<Plus className="size-5 stroke-[3] text-white" />
					}
					onClick={() => setIsModalOpen(true)}
				/>
			</div>

			<div className="w-full space-y-2">
				{isFetching && (
					<p className="animate-pulse px-2 text-sm font-black uppercase tracking-widest text-stone-400">
						Carregando empresas...
					</p>
				)}

				{!isFetching &&
					filteredList.map((company) => (
						<CompanyListItem
							key={company.id}
							company={company}
							onEdit={setCompanyToEdit}
							onNavigateToDetails={(id) =>
								navigate(`/admin/empresas/${id}`)
							}
						/>
					))}

				{!isFetching && filteredList.length === 0 && (
					<p className="px-2 text-sm text-stone-400">
						Nenhuma empresa encontrada.
					</p>
				)}
			</div>

			<CompanyModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
			/>

			<EditCompanyModal
				isOpen={!!companyToEdit}
				onClose={() => setCompanyToEdit(null)}
				onSuccess={() => setCompanyToEdit(null)}
				company={companyToEdit}
			/>
		</div>
	);
};

export { Companies };
